/**
 * Notebook feature hooks — page tree, single page read/write, archive /
 * restore / hard-delete, task↔page reference list + attach/detach.
 *
 * Conventions:
 * - Query keys: ['page-tree', wsId], ['page', wsId, slug], ['page', wsId, pageId],
 *   ['page-tasks', pageId], ['task-page-refs', taskId].
 * - Optimistic mutations on move/update; on 409 we refetch the source of truth.
 * - `idempotencyKey` is set on every create so MCP and double-clicks are safe.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/lib/api'
import type { Page, Task, TaskPageRef } from '@friday/shared/realtime'

/** Flat list of pages for the workspace (used to build the sidebar tree). */
export function usePageTree(wsId: string | undefined, opts: { includeArchived?: boolean } = {}) {
  return useQuery({
    queryKey: ['page-tree', wsId, opts.includeArchived ? 'all' : 'active'],
    queryFn: () =>
      api<Page[]>(`/api/v1/workspaces/${wsId}/pages`, {
        query: { includeArchived: opts.includeArchived ? 'true' : undefined, shape: 'flat' },
      }),
    enabled: !!wsId,
    staleTime: 30_000,
  })
}

/** Single page read by id. The URL key for v1 is the page id; slugs come
 * in a later phase when the SPA ships slugs in URLs. */
export function usePage(wsId: string | undefined, pageId: string | undefined) {
  return useQuery({
    queryKey: ['page', wsId, pageId],
    queryFn: () => api<Page>(`/api/v1/pages/${pageId}`),
    enabled: !!wsId && !!pageId,
  })
}

export function useCreatePage(wsId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      parentPageId?: string | null
      title: string
      icon?: string | null
      bodyMd?: string | null
      slug?: string
      prevPageId?: string | null
      nextPageId?: string | null
    }) =>
      api<Page>(`/api/v1/workspaces/${wsId}/pages`, {
        method: 'POST',
        body: input,
        idemKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['page-tree', wsId] })
    },
  })
}

export function useUpdatePage(wsId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      pageId: string
      version: number
      patch: { title?: string; icon?: string | null; bodyMd?: string | null; slug?: string }
    }) =>
      api<Page>(`/api/v1/pages/${input.pageId}?version=${input.version}`, {
        method: 'PATCH',
        body: input.patch,
      }),
    onMutate: async (input) => {
      const treeKey = ['page-tree', wsId]
      await qc.cancelQueries({ queryKey: treeKey })
      const previousTree = qc.getQueryData<Page[]>(treeKey)
      if (previousTree) {
        qc.setQueryData<Page[]>(
          treeKey,
          previousTree.map((p) => (p.id === input.pageId ? { ...p, ...input.patch, version: input.version + 1 } : p)),
        )
      }
      return { previousTree }
    },
    onError: (_e, _input, ctx) => {
      if (ctx?.previousTree) qc.setQueryData(['page-tree', wsId], ctx.previousTree)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['page-tree', wsId] })
      void qc.invalidateQueries({ queryKey: ['page', wsId] })
    },
  })
}

export function useArchivePage(wsId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pageId: string) => api<Page>(`/api/v1/pages/${pageId}/archive`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['page-tree', wsId] })
    },
  })
}

export function useRestorePage(wsId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pageId: string) => api<Page>(`/api/v1/pages/${pageId}/restore`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['page-tree', wsId] })
    },
  })
}

export function useDeletePageHard(wsId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pageId: string) =>
      api<{ ok: true }>(`/api/v1/pages/${pageId}`, { method: 'DELETE', query: { hard: 'true' } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['page-tree', wsId] })
    },
  })
}

/** Move / re-parent a page. The server enforces no-cycle and same-
 *  workspace; the `expectedVersion` query param is optimistic-
 *  concurrency — on 409 the caller can refetch and retry. */
export function useMovePage(wsId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      pageId: string
      version: number
      parentPageId: string | null
      prevPageId?: string | null
      nextPageId?: string | null
    }) =>
      api<Page>(`/api/v1/pages/${input.pageId}/move?version=${input.version}`, {
        method: 'POST',
        body: {
          parentPageId: input.parentPageId,
          prevPageId: input.prevPageId ?? null,
          nextPageId: input.nextPageId ?? null,
        },
      }),
    onMutate: async (input) => {
      const treeKey = ['page-tree', wsId]
      await qc.cancelQueries({ queryKey: treeKey })
      const previousTree = qc.getQueryData<Page[]>(treeKey)
      if (previousTree) {
        qc.setQueryData<Page[]>(
          treeKey,
          previousTree.map((p) =>
            p.id === input.pageId ? { ...p, parentPageId: input.parentPageId, version: input.version + 1 } : p,
          ),
        )
      }
      return { previousTree }
    },
    onError: (_e, _input, ctx) => {
      if (ctx?.previousTree) qc.setQueryData(['page-tree', wsId], ctx.previousTree)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['page-tree', wsId] })
    },
  })
}

/** Reverse lookup: tasks that reference this page. */
export function usePageTasks(pageId: string | undefined) {
  return useQuery({
    queryKey: ['page-tasks', pageId],
    queryFn: () => api<Task[]>(`/api/v1/pages/${pageId}/tasks`),
    enabled: !!pageId,
  })
}

/** List pages a task references. */
export function useTaskPageRefs(taskId: string | undefined) {
  return useQuery({
    queryKey: ['task-page-refs', taskId],
    queryFn: () => api<TaskPageRef[]>(`/api/v1/tasks/${taskId}/pages`),
    enabled: !!taskId,
  })
}

export function useAttachPageToTask(wsId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { taskId: string; pageId: string; note?: string }) =>
      api<{ ok: true }>(`/api/v1/tasks/${input.taskId}/pages`, {
        method: 'POST',
        body: { pageId: input.pageId, note: input.note },
        idemKey: crypto.randomUUID(),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['task-page-refs', vars.taskId] })
      void qc.invalidateQueries({ queryKey: ['page-tasks', vars.pageId] })
    },
  })
}

export function useDetachPageFromTask(wsId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { taskId: string; pageId: string }) =>
      api<{ ok: true }>(`/api/v1/tasks/${input.taskId}/pages/${input.pageId}`, { method: 'DELETE' }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['task-page-refs', vars.taskId] })
      void qc.invalidateQueries({ queryKey: ['page-tasks', vars.pageId] })
    },
  })
}
